import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { faCheckCircle, faCircle } from '@fortawesome/pro-regular-svg-icons';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { BehaviorSubject, Observable } from 'rxjs';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { ConfigurableEditorSettingRemoteDto } from '../../../../../generated/swagger';
import { EditorType } from '../../../../samwebDescription/samweb/editorType';
import { displayHide, moveInAndOut } from '../../animation/animations';

@Component({
   selector: 'samweb-configurable-editor',
   templateUrl: './configurable-editor.component.html',
   styleUrls: ['./configurable-editor.component.scss'],
   animations: [moveInAndOut, displayHide],
})
export class ConfigurableEditorComponent implements OnInit, OnChanges {
   public toggleButtonActiveIcon = faCheckCircle;
   public toggleButtonNonActiveIcon = faCircle;

   @Input()
   public selectedEditorType: EditorType;

   @Input()
   public editorSettings: ConfigurableEditorSettingRemoteDto;

   @Input()
   public isEditorReadOnly: boolean;

   @Input()
   public editorValue: string;

   @Input()
   public clearValue: string;

   @Input()
   public isClearButtonVisible: boolean;

   @Input()
   // just set this if you can trust the input (editorValue) 100%
   // this is very important to pe safe against xss vulnerability
   public isInputSafe = false;

   @Output()
   public editorValueChanged = new EventEmitter<string>();

   @Output()
   public isUpdatingValue = new EventEmitter<boolean>();

   public checkboxEnabled$: Observable<boolean>;

   public clearTagButtonIcon = WellKnownIconsEnum.CLEAR.iconSmall;
   @ViewChild(ButtonDirective)
   public toggleButton: ButtonDirective;

   @ViewChild('cellTooltipDirective')
   public cellTooltipDirective: TooltipDirective;

   private isEmittingOfValueAllowed = false;
   public currentValue$: BehaviorSubject<string> = new BehaviorSubject<string>('');

   constructor() {}
   ngOnInit(): void {
      this.updateCurrentTextOfEditor(this.editorValue);
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes.editorValue?.previousValue !== changes.editorValue?.currentValue) {
         this.updateCurrentTextOfEditor(this.editorValue);
      }
   }

   updateCurrentTextOfEditor(value: string) {
      this.currentValue$.next(value);
   }

   valueChangeString(eventTargetValue: string) {
      this.setInternalValue(eventTargetValue);
   }

   valueChangeNumeric(eventTargetValue: number) {
      this.setInternalValue(eventTargetValue.toString());
   }

   valueChangeHtml(eventTargetValue: string) {
      this.setInternalValue(eventTargetValue);
   }

   setInternalValue(value: string) {
      this.editorIsUpdatingValue(true);
      this.updateCurrentTextOfEditor(value);
      this.isEmittingOfValueAllowed = true;
   }

   valueChangeStringDebounced(eventTargetValue: string) {
      this.editorChangeCurrentValueIfPossible();
   }

   valueChangeNumericDebounced(eventTargetValue: number) {
      this.editorChangeCurrentValueIfPossible();
   }

   valueChangeHtmlDebounced(eventTargetValue: string) {
      this.editorChangeCurrentValueIfPossible();
   }

   valueChangeSelection(eventTargetValue: string) {
      this.updateCurrentTextOfEditor(eventTargetValue);
      this.editorChangeCurrentValue();
   }

   toggleButtonClicked($event: PointerEvent, editorSettings: ConfigurableEditorSettingRemoteDto) {
      if (this.toggleButton.selected) {
         this.updateCurrentTextOfEditor('');
      } else {
         this.updateCurrentTextOfEditor(editorSettings.checkboxValue);
      }
      this.editorChangeCurrentValue();
   }

   editorIsUpdatingValue(value: boolean) {
      this.isUpdatingValue.emit(value);
   }

   editorChangeCurrentValueIfPossible() {
      if (!this.isEmittingOfValueAllowed) {
         return;
      }
      this.editorChangeCurrentValue();
   }

   editorChangeCurrentValue() {
      this.editorValueChanged.emit(this.currentValue$.getValue());
   }

   clear() {
      this.isEmittingOfValueAllowed = false;
      this.updateCurrentTextOfEditor(this.clearValue);
   }

   afterClear() {
      this.editorChangeCurrentValue();
   }

   public showTooltip(e: MouseEvent): void {
      const element = e.target as HTMLElement;
      if (element.offsetWidth < element.scrollWidth) {
         this.cellTooltipDirective.show(element);
      } else {
         this.cellTooltipDirective.hide();
      }
   }

   public hideTooltip() {
      this.cellTooltipDirective.hide();
   }
}
